<template>
  <p v-if="oneToOne" class="comment-seen-by comment-seen-by--text" data-testid="comment-seen-text">
    {{ statusLine }}
  </p>
  <div
    v-else-if="seenBy.length"
    class="comment-seen-by comment-seen-by--avatars"
    role="group"
    :aria-label="`Seen by ${names}`"
    data-testid="comment-seen-avatars"
  >
    <ExAvatar
      v-for="viewer in shown"
      :key="viewer.id"
      class="comment-seen-by__avatar"
      size="small"
      :picture="viewer.avatar"
      :first-name="firstName(viewer)"
      :last-name="lastName(viewer)"
      :title="viewer.name"
    />
    <span v-if="extra > 0" class="comment-seen-by__extra" data-testid="comment-seen-extra">
      +{{ extra }}
    </span>
  </div>
</template>

<script setup>
import { computed } from "vue";
import ExAvatar from "@components/common/ExAvatar.vue";
import { formatCommentTime } from "@components/ChatterDrawer/composables/commentUtils";

const MAX_AVATARS = 5;

const props = defineProps({
  /** `seen_by` rows from the comment payload: { id, name, avatar, seen_at, … }. */
  seenBy: { type: Array, default: () => [] },
  /** Direct one-to-one thread: show a text status line instead of avatars. */
  oneToOne: { type: Boolean, default: false },
  /** ISO timestamp the comment was sent, for the relative time in the text line. */
  sentAt: { type: String, default: "" },
});

const shown = computed(() => props.seenBy.slice(0, MAX_AVATARS));
const extra = computed(() => Math.max(0, props.seenBy.length - MAX_AVATARS));
const names = computed(() => props.seenBy.map((v) => v.name).join(", "));

const statusLine = computed(() => {
  const status = props.seenBy.length ? "Seen" : "Not seen yet";
  const sent = formatCommentTime(props.sentAt);
  return sent ? `${status} · ${sent}` : status;
});

const nameParts = (viewer) => (viewer.name || "").trim().split(/\s+/);
const firstName = (viewer) => nameParts(viewer)[0] || "";
const lastName = (viewer) => nameParts(viewer).slice(1).join(" ");
</script>

<style scoped>
.comment-seen-by {
  margin: 0;
  font-family: Inter;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #667085;
}

.comment-seen-by--avatars {
  display: flex;
  align-items: center;
  gap: 4px;
}

.comment-seen-by__extra {
  font-weight: 500;
  color: #475467;
}
</style>
